import { useCallback, useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { CheckCircle2, XCircle, ChevronDown, ChevronUp, RefreshCw, ListChecks } from "lucide-react";
import api from "../lib/api";

export function ReadStatus({ examId, refreshKey }) {
  const [data, setData] = useState(null);
  const [openCls, setOpenCls] = useState(null);
  const [showMissingOnly, setShowMissingOnly] = useState(true);

  const load = useCallback(() => {
    if (!examId) { setData(null); return; }
    api.get(`/exams/${examId}/read-status`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [examId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (!examId || !data) return null;
  const pct = data.total ? Math.round((data.read / data.total) * 100) : 0;
  return (
    <Card className="p-4 bg-card border-border mb-6" data-testid="read-status-panel">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h3 className="font-semibold flex items-center gap-2"><ListChecks className="h-5 w-5 text-primary" />Toplu Okuma Takibi</h3>
        <Badge className={pct === 100 ? "bg-emerald-500 text-white" : "bg-primary/10 text-primary"} data-testid="read-status-total">{data.read}/{data.total} okundu · %{pct}</Badge>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setShowMissingOnly((v) => !v)} data-testid="read-status-toggle-missing">{showMissingOnly ? "Tümünü göster" : "Sadece eksikler"}</Button>
          <Button variant="ghost" size="sm" onClick={load} data-testid="read-status-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden mb-4"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} /></div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {data.classes.map((c) => {
          const key = `${c.grade}-${c.branch}`;
          const open = openCls === key;
          const done = c.missing === 0;
          return (
            <div key={key} className={`rounded-lg border ${done ? "border-emerald-500/60 bg-emerald-500/5" : "border-border bg-background"}`} data-testid={`read-status-class-${key}`}>
              <button className="w-full flex items-center gap-2 p-3 text-left" onClick={() => setOpenCls(open ? null : key)} data-testid={`read-status-class-btn-${key}`}>
                <span className="font-bold">{key}</span>
                <span className={`text-xs font-semibold ${done ? "text-emerald-600" : "text-amber-600"}`}>{c.read}/{c.total}</span>
                {!done && <span className="text-xs text-muted-foreground">· {c.missing} eksik</span>}
                {done ? <CheckCircle2 className="h-4 w-4 text-emerald-500 ml-auto" /> : (open ? <ChevronUp className="h-4 w-4 ml-auto" /> : <ChevronDown className="h-4 w-4 ml-auto" />)}
              </button>
              {open && (
                <ul className="border-t border-border max-h-64 overflow-auto text-sm divide-y divide-border">
                  {c.students.filter((s) => !showMissingOnly || !s.read).map((s) => (
                    <li key={s.id} className="flex items-center gap-2 px-3 py-1.5" data-testid={`read-status-student-${s.id}`}>
                      {s.read ? <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" /> : <XCircle className="h-4 w-4 text-rose-500 shrink-0" />}
                      <span className="font-mono text-xs text-muted-foreground w-8">{s.number}</span>
                      <span className={`truncate ${s.read ? "" : "font-semibold"}`}>{s.name}</span>
                      {s.read && <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">Net {s.total_net} · {s.score}p</span>}
                    </li>
                  ))}
                  {showMissingOnly && c.students.every((s) => s.read) && <li className="px-3 py-2 text-xs text-emerald-600">Bu sınıfta eksik yok ✓</li>}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
